import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminScanSequencesService } from '../../services/admin/admin-scan-sequences.service';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OvNoContent from '../UI/molecules/OvNoContent';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvScanSequencesList from '../UI/organisms/OvScanSequencesList';
import OvButton from '../UI/atoms/OvButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Variables from '../../design-system/variables';
import OvCreateScanSequenceDialog from '../UI/molecules/OvCreateScanSequenceDialog';
import { CreateScanSequenceRequest } from '../../common/model/dto/scan-sequences/create-scan-sequence-request.dto';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { useAdminScanSequenceMutations } from '../../hooks/use-admin-scan-sequence-mutations';
import { useAdminScanSequenceMutationStatus } from '../../hooks/use-admin-scan-sequence-mutation-status';
import { getAxiosErrorMessage } from '../../common/utils/helpers';

const AdminScanSequencesList = () => {
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const scanningPlanOperations = useAuthorized(Resource.ScanningPlan);

  const scanningPlanCreateAllowed =
    scanningPlanOperations.create || applicationOperations.supervise;

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [offset, setOffset] = useQueryParam(
    'offset',
    withDefault(NumberParam, 0),
    { updateType: 'replaceIn' }
  );

  const query = queryString.stringify({
    limit: AdminScanSequencesService.SCAN_SEQUENCE_LIST_LIMIT,
    order_by: 'order',
    order_dir: 'asc',
    offset,
  });
  const {
    data: paginatedScanSequences,
    isFetching: isScanSequenceListFetching,
    error: scanSequenceListError,
  } = useQuery({
    queryKey: ['admin-scan-sequences', query],
    queryFn: () => AdminScanSequencesService.loadScanSequencesByQuery(query),
  });
  const scanSequenceList = paginatedScanSequences?.docs ?? [];

  const { createScanSequence } = useAdminScanSequenceMutations();
  const { isMutating, error: mutationError } =
    useAdminScanSequenceMutationStatus();
  const isLoading = isScanSequenceListFetching || isMutating;
  const error = scanSequenceListError ?? mutationError;

  const goToPage = (event: any, value: number) => {
    setOffset(
      (value - 1) * AdminScanSequencesService.SCAN_SEQUENCE_LIST_LIMIT ||
        undefined
    );
  };

  const handleOnSave = (
    createScanSequenceRequest: CreateScanSequenceRequest
  ) => {
    createScanSequence.mutate(createScanSequenceRequest);
    setIsDialogOpen(false);
  };

  return (
    <Container>
      <OvPageTitle
        title={t('app.subPages.menu.scanSequences')}
        icon={<ChecklistIcon />}
      />

      <ActionBar>
        <OvPaginationBar
          total={paginatedScanSequences?.total ?? 0}
          limit={AdminScanSequencesService.SCAN_SEQUENCE_LIST_LIMIT}
          offset={offset}
          onChange={goToPage}
        />
        {scanningPlanCreateAllowed && (
          <StyledOvButton onClick={() => setIsDialogOpen(true)}>
            <AddCircleIcon style={{ marginRight: '5' }} />
            {t('scanSequenceDetails.createScanSequence')}
          </StyledOvButton>
        )}
      </ActionBar>

      {error && (
        <StyledErrorMessage>
          <StyledErrorOutlineIcon />
          {getAxiosErrorMessage(error)}
        </StyledErrorMessage>
      )}

      {scanSequenceList?.length ? (
        <OvScanSequencesList scanSequences={scanSequenceList} />
      ) : (
        !isLoading && <OvNoContent>{t('common.noContent')}</OvNoContent>
      )}

      {isLoading && <OvLoadingIndicator position="fixed" />}

      <OvCreateScanSequenceDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
      />
    </Container>
  );
};

export default AdminScanSequencesList;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
`;

const StyledErrorMessage = styled.p`
  color: ${Colours.OV_RED};
  margin: 10px 0 24px 0;
  display: flex;
  align-items: center;
`;

const StyledErrorOutlineIcon = styled(ErrorOutlineIcon)`
  margin-right: 0.5rem;
`;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    padding: 0.125rem 0.7rem 0.125rem 0.35rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;
